import React, { useEffect, useMemo, useState } from 'react';
import { placementsAPI, placementDocsAPI } from '../lib/api';
import { toast } from 'sonner';
import { bySurname, byName, surnameKey } from '../lib/sort';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Search, GraduationCap, Building2, Undo2, Users, X, CheckCircle2, Mail, FileText, Pencil } from 'lucide-react';
import PlacementDetailsDialog from './PlacementDetailsDialog';

// Pill config for the placement-letter status footer on each board card.
// Kept small so the pill fits inside a compact placement card.
export const LETTER_PILL = {
  signed: { cls: 'bg-emerald-100 text-emerald-700', label: 'Signed', Icon: CheckCircle2 },
  viewed: { cls: 'bg-sky-100 text-sky-700', label: 'Viewed', Icon: FileText },
  sent: { cls: 'bg-amber-100 text-amber-800', label: 'Sent', Icon: Mail },
  draft: { cls: 'bg-gray-100 text-gray-600', label: 'Draft', Icon: FileText },
  voided: { cls: 'bg-red-50 text-red-700', label: 'Voided', Icon: FileText },
  not_created: { cls: 'bg-white text-gray-400 border border-dashed border-gray-300', label: 'No letter', Icon: FileText },
};

/**
 * Cycle Board — Kanban-style drag & drop placement builder for a cycle.
 *
 * Left column: unplaced students in this cohort
 * Right columns: participating restaurants with open slots
 *
 * Dropping a student on a restaurant instantly creates a placement using
 * the cycle's default dates and a fallback "Placement" position. Dropping
 * a placement card from one restaurant onto another re-assigns it via a
 * PATCH. A sonner toast surfaces success + an Undo (which deletes the
 * newly-created placement).
 *
 * Props:
 *   cycle: full CycleDetail object (contains .students, .restaurants,
 *          .placements, .start_date, .end_date, .status, .restaurant_ids)
 *   canWrite: boolean — disables drag/drop when false (read-only, archived, etc.)
 *   onChange: () => void — called after any mutation so the parent reloads
 */
const CycleBoard = ({ cycle, canWrite, onChange, onRestaurantClick }) => {
  const [q, setQ] = useState('');
  const [dragging, setDragging] = useState(null); // { studentId, fromPlacementId? }
  const [dragOverRestaurantId, setDragOverRestaurantId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detailsPlacementId, setDetailsPlacementId] = useState(null);

  // Load per-placement document status so each card can show letter state
  // (draft / sent / signed) inline. Refreshed whenever the cycle payload
  // changes so create/reassign/undo actions stay in sync.
  const [docRows, setDocRows] = useState([]);
  useEffect(() => {
    let cancelled = false;
    placementDocsAPI.overview().then((r) => {
      if (!cancelled) setDocRows(r.data || []);
    }).catch(() => { /* soft-fail — cards just won't show the letter pill */ });
    return () => { cancelled = true; };
  }, [cycle.placements]);
  const docsByPlacement = useMemo(() => {
    const m = {};
    docRows.forEach((r) => { m[r.placement_id] = r; });
    return m;
  }, [docRows]);

  const cyclePlacements = useMemo(
    () => (cycle.placements || []).filter((p) => p.status === 'active' || p.status === 'pending'),
    [cycle.placements],
  );

  const placedStudentIds = useMemo(
    () => new Set(cyclePlacements.map((p) => p.student_id)),
    [cyclePlacements],
  );

  const unplacedStudents = useMemo(() => {
    const query = q.trim().toLowerCase();
    return (cycle.students || [])
      .filter((s) => !placedStudentIds.has(s.id))
      .filter((s) => {
        if (!query) return true;
        return (
          (s.name || '').toLowerCase().includes(query) ||
          (s.student_number || '').toLowerCase().includes(query) ||
          (s.email || '').toLowerCase().includes(query)
        );
      })
      .sort(bySurname);
  }, [cycle.students, placedStudentIds, q]);

  const studentById = useMemo(() => {
    const m = {};
    (cycle.students || []).forEach((s) => { m[s.id] = s; });
    return m;
  }, [cycle.students]);

  const placementsByRestaurant = useMemo(() => {
    const map = {};
    cyclePlacements.forEach((p) => {
      (map[p.restaurant_id] = map[p.restaurant_id] || []).push(p);
    });
    const key = (p) => surnameKey(studentById[p.student_id]?.name || p.student_name);
    Object.values(map).forEach((list) => list.sort((a, b) => key(a).localeCompare(key(b), undefined, { sensitivity: 'base' })));
    return map;
  }, [cyclePlacements, studentById]);

  const restaurantsSorted = useMemo(
    () => [...(cycle.restaurants || [])].sort(byName),
    [cycle.restaurants],
  );

  // Delete a placement — student returns to the "Unplaced" column. Backed
  // by a toast Undo which re-creates the placement with the same restaurant
  // and dates so accidental removals are recoverable.
  const removePlacement = async (placement, restaurantName) => {
    if (!canWrite || busy) return;
    const studentName = studentById[placement.student_id]?.name || 'Student';
    setBusy(true);
    try {
      await placementsAPI.delete(placement.id);
      toast.success(`${studentName} removed from ${restaurantName}`, {
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await placementsAPI.create({
                student_id: placement.student_id,
                restaurant_id: placement.restaurant_id,
                cycle_id: cycle.id,
                position: placement.position || 'Placement',
                start_date: placement.start_date || cycle.start_date,
                end_date: placement.end_date || cycle.end_date,
                notes: '',
              });
              toast.success('Removal undone');
              onChange?.();
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Undo failed');
            }
          },
        },
      });
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove placement');
    } finally {
      setBusy(false);
    }
  };

  // ------- Drop handlers ------------------------------------------------

  const dropStudentOnRestaurant = async (restaurant) => {
    if (!canWrite || !dragging) return;
    const filled = (placementsByRestaurant[restaurant.id] || []).length;
    const open = Math.max((restaurant.available_positions || 0) - filled, 0);
    if (open <= 0 && !dragging.fromPlacementId) {
      toast.error(`${restaurant.name} has no open slots.`);
      return;
    }
    setBusy(true);
    try {
      if (dragging.fromPlacementId) {
        // Re-assign: move placement to a different restaurant
        if (dragging.fromRestaurantId === restaurant.id) return; // no-op
        await placementsAPI.update(dragging.fromPlacementId, {
          restaurant_id: restaurant.id,
        });
        toast.success(`Moved ${studentById[dragging.studentId]?.name || 'student'} → ${restaurant.name}`);
        onChange?.();
      } else {
        // Create a fresh placement using the cycle's default dates
        const payload = {
          student_id: dragging.studentId,
          restaurant_id: restaurant.id,
          cycle_id: cycle.id,
          position: 'Placement',
          start_date: cycle.start_date,
          end_date: cycle.end_date,
          notes: '',
        };
        const res = await placementsAPI.create(payload);
        const createdId = res?.data?.id;
        const studentName = studentById[dragging.studentId]?.name || 'Student';
        toast.success(`${studentName} placed at ${restaurant.name}`, {
          action: createdId ? {
            label: 'Undo',
            onClick: async () => {
              try {
                await placementsAPI.delete(createdId);
                toast.success('Placement undone');
                onChange?.();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Undo failed');
              }
            },
          } : undefined,
        });
        onChange?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Placement failed');
    } finally {
      setBusy(false);
      setDragging(null);
      setDragOverRestaurantId(null);
    }
  };

  // ------- Render -------------------------------------------------------

  if ((cycle.restaurants || []).length === 0) {
    return (
      <Card><CardContent className="py-10 text-center text-gray-500">
        <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
        No restaurants attached to this cycle yet. Add at least one restaurant to start placing students.
      </CardContent></Card>
    );
  }
  if ((cycle.students || []).length === 0) {
    return (
      <Card><CardContent className="py-10 text-center text-gray-500">
        <GraduationCap className="w-8 h-8 mx-auto mb-2 opacity-40" />
        No students in this cohort yet. Add students that match the cycle's cohort criteria.
      </CardContent></Card>
    );
  }

  return (
    <div data-testid="cycle-board" className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
      {/* Unplaced students column */}
      <Card className="min-h-[400px]">
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-ica-navy" />
              <h3 className="font-semibold text-sm text-ica-navy">Unplaced students</h3>
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-wide rounded-full bg-ica-navy/10 text-ica-navy px-2 py-0.5">
              {unplacedStudents.length}
            </span>
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              data-testid="board-student-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="pl-7 h-8 text-xs"
            />
          </div>
          <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
            {unplacedStudents.length === 0 ? (
              <p className="text-xs text-gray-400 italic text-center py-6">
                {(cycle.students || []).length === placedStudentIds.size
                  ? 'Every student is already placed. 🎉'
                  : 'No students match this search.'}
              </p>
            ) : unplacedStudents.map((s) => (
              <div
                key={s.id}
                data-testid={`board-student-${s.id}`}
                draggable={canWrite && !busy}
                onDragStart={(e) => {
                  setDragging({ studentId: s.id });
                  e.dataTransfer.effectAllowed = 'move';
                  // Firefox refuses to initiate a drag unless SOME data is
                  // set. A no-op text payload keeps every browser happy.
                  e.dataTransfer.setData('text/plain', `student:${s.id}`);
                }}
                onDragEnd={() => { setDragging(null); setDragOverRestaurantId(null); }}
                className={`rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm select-none ${canWrite ? 'cursor-grab active:cursor-grabbing hover:border-ica-navy hover:shadow' : 'cursor-not-allowed opacity-70'}`}
              >
                <div className="font-medium text-gray-900 truncate">{s.name}</div>
                <div className="text-[10px] text-gray-500 truncate">{s.student_number}{s.email ? ` · ${s.email}` : ''}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Restaurants columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 auto-rows-min">
        {restaurantsSorted.map((r) => {
          const rp = placementsByRestaurant[r.id] || [];
          const total = r.available_positions || 0;
          const filled = rp.length;
          const open = Math.max(total - filled, 0);
          const isDropTarget = dragOverRestaurantId === r.id;
          const isFull = open <= 0 && !dragging?.fromPlacementId;
          return (
            <div
              key={r.id}
              data-testid={`board-restaurant-${r.id}`}
              onDragOver={(e) => {
                if (!canWrite || !dragging) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setDragOverRestaurantId(r.id);
              }}
              onDragLeave={() => setDragOverRestaurantId((cur) => (cur === r.id ? null : cur))}
              onDrop={(e) => {
                e.preventDefault();
                dropStudentOnRestaurant(r);
              }}
              className={`rounded-lg border-2 transition-colors ${
                isDropTarget
                  ? (isFull ? 'border-red-400 bg-red-50/60' : 'border-ica-navy bg-ica-navy/5')
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                <button
                  type="button"
                  data-testid={`board-restaurant-open-${r.id}`}
                  onClick={() => onRestaurantClick?.(r)}
                  title={`Open ${r.name} — edit positions, contacts and details`}
                  className="min-w-0 flex-1 text-left group/rest rounded -mx-1 px-1 hover:bg-ica-navy/5 transition-colors"
                >
                  <div className="font-semibold text-sm text-ica-navy truncate inline-flex items-center gap-1 max-w-full">
                    <span className="truncate">{r.name}</span>
                    <Pencil className="w-3 h-3 shrink-0 opacity-0 group-hover/rest:opacity-60 transition-opacity" />
                  </div>
                  <div className="text-[10px] text-gray-500 truncate">{r.address || '—'}</div>
                </button>
                <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 shrink-0 ml-2 ${
                  open <= 0 ? 'bg-red-100 text-red-700' : open <= 1 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {filled}/{total} · {open} open
                </span>
              </div>

              <div className="p-2 space-y-1.5 min-h-[80px]">
                {rp.length === 0 ? (
                  <div className={`text-center py-3 text-[11px] italic ${isDropTarget ? 'text-ica-navy font-medium' : 'text-gray-400'}`}>
                    {isDropTarget ? 'Drop here to place' : 'Drop a student here'}
                  </div>
                ) : rp.map((p) => {
                  const s = studentById[p.student_id];
                  const isAcknowledged = (p.ack_status || '').toLowerCase() === 'signed'
                    || (p.status || '').toLowerCase() === 'active';
                  const docRow = docsByPlacement[p.id];
                  const letter = docRow?.documents?.placement_letter;
                  const letterStatus = (letter?.status || 'not_created').toLowerCase();
                  const letterPill = LETTER_PILL[letterStatus] || LETTER_PILL.not_created;
                  return (
                    <div
                      key={p.id}
                      data-testid={`board-placement-${p.id}`}
                      draggable={canWrite && !busy}
                      onDragStart={(e) => {
                        setDragging({ studentId: p.student_id, fromPlacementId: p.id, fromRestaurantId: r.id });
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', `placement:${p.id}`);
                      }}
                      onDragEnd={() => { setDragging(null); setDragOverRestaurantId(null); }}
                      onClick={() => setDetailsPlacementId(p.id)}
                      className={`group relative rounded border border-gray-200 bg-gray-50 hover:bg-white px-2 py-1.5 text-xs ${canWrite ? 'cursor-pointer active:cursor-grabbing' : 'cursor-pointer'}`}
                    >
                      <div className="flex items-start justify-between gap-1.5">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900 truncate">{s?.name || p.student_name}</div>
                          <div className="text-[10px] text-gray-500 truncate">{s?.student_number || ''} · {p.position || 'Placement'}</div>
                        </div>
                        {canWrite && (
                          <button
                            type="button"
                            data-testid={`board-remove-${p.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              removePlacement(p, r.name);
                            }}
                            title={`Remove ${s?.name || 'student'} from ${r.name}`}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-600 focus:opacity-100 focus:outline-none rounded p-0.5"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      {/* Status footer: acknowledged check + student placement letter pill */}
                      <div className="mt-1 flex items-center gap-1.5">
                        <span
                          data-testid={`board-ack-${p.id}`}
                          title={isAcknowledged ? 'Placement confirmed' : 'Awaiting acknowledgement'}
                          className={`inline-flex items-center gap-0.5 text-[9px] font-medium ${
                            isAcknowledged ? 'text-emerald-600' : 'text-gray-400'
                          }`}
                        >
                          <CheckCircle2 className="w-3 h-3" />
                          {isAcknowledged ? 'Confirmed' : 'Pending'}
                        </span>
                        <span
                          data-testid={`board-letter-${p.id}`}
                          title={`Student placement letter: ${letterPill.label}`}
                          className={`inline-flex items-center gap-0.5 text-[9px] font-medium rounded px-1 py-[1px] ${letterPill.cls}`}
                        >
                          <letterPill.Icon className="w-2.5 h-2.5" />
                          {letterPill.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {/* Show the drop hint even when there are placements, if actively dragging */}
                {rp.length > 0 && isDropTarget && (
                  <div className="text-center py-1.5 text-[10px] italic text-ica-navy font-medium border border-dashed border-ica-navy/40 rounded">
                    {dragging?.fromPlacementId ? 'Reassign here' : 'Add another placement'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="lg:col-span-2 flex items-center gap-2 text-[11px] text-gray-500">
        <Undo2 className="w-3 h-3" />
        <span>Click a restaurant name to edit its positions and details. Click a placement card to see the full details. Drag between restaurants to reassign. Hover over a card to reveal the ✕ remove button. Every mutation includes an Undo in the toast.</span>
      </div>

      <PlacementDetailsDialog
        open={!!detailsPlacementId}
        onOpenChange={(o) => { if (!o) setDetailsPlacementId(null); }}
        placementId={detailsPlacementId}
      />
    </div>
  );
};

export default CycleBoard;
